import React, { useState, useRef } from 'react';
import { toast } from 'react-toastify';
import { withScreen } from './Screen';
import { Button } from './common/Button';
import { useRequestManager } from './common/RequestManager';
import { Helmet } from 'react-helmet';
import { api } from '../utils/api';

interface ImportTransactionsPageProps {}

function ImportTransactionsPage({}: ImportTransactionsPageProps) {
  const { trackRequest } = useRequestManager();
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [platform, setPlatform] = useState<'temu' | 'leboncoin' | 'packncarry'>('temu');
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setCsvFile(file);
    }
  };

  const handleFileRemove = () => {
    setCsvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!csvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      formData.append('platform', platform);

      const response = await trackRequest(
        api.post('/transactions/import', formData),
        {
          title: 'Importing transactions',
          isBlocking: true,
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success(`Successfully imported ${result.importedCount} transaction(s)`);
        if (result.errors && result.errors.length > 0) {
          toast.warning(`Some errors occurred: ${result.errors.join(', ')}`);
        }
        handleFileRemove();
      } else {
        toast.error(`Import failed: ${result.errors?.join(', ') || 'Unknown error'}`);
      }
    } catch (error: any) {
      console.error('Error importing transactions:', error);
      const errorMessage = error.data?.errors?.join(', ') || error.message || 'Unknown error';
      toast.error(`Failed to import transactions: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Import Transactions - Wow Magasin</title>
      </Helmet>
      <div className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">Import Transactions</h1>
        
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Platform
            </label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value as 'temu' | 'leboncoin' | 'packncarry')}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm"
              disabled={isUploading}
            >
              <option value="temu">Temu</option>
              <option value="leboncoin">Leboncoin</option>
              <option value="packncarry">Pack & Carry</option>
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <div className="flex items-center gap-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  cursor-pointer"
                disabled={isUploading}
              />
            </div>
            {csvFile && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md flex items-center justify-between">
                <span className="text-sm text-gray-700">
                  Selected: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(2)} KB)
                </span>
                <Button
                  preset="danger-light"
                  size="sm"
                  onClick={handleFileRemove}
                  disabled={isUploading}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>

          <div className="mb-6">
            <h2 className="text-lg font-semibold mb-2">CSV Format</h2>
            <p className="text-sm text-gray-600 mb-2">
              Your CSV file should have the following columns:
            </p>
            <div className="bg-gray-50 p-4 rounded-md">
              <code className="text-xs text-gray-800">
                type, currency, amount, amountVat, billDate, source, comment, orderId, lineItemId, externalId, externalLineItemId, isConfirmed, originCountry, destinationCountry
              </code>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Required fields: type, currency, amount, amountVat, billDate, source
              <br />
              Optional fields: comment, orderId, lineItemId, externalId, externalLineItemId, isConfirmed, originCountry (defaults to FR), destinationCountry (defaults to FR)
            </p>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              preset="primary"
              onClick={handleImport}
              disabled={!csvFile || isUploading}
            >
              {isUploading ? 'Importing...' : 'Import Transactions'}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}

export default withScreen(ImportTransactionsPage);